"use client";
import React, {
  FC,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
} from "react";
import { Button, Modal } from "@arco-design/web-react";
import {
  TProductSkuPropsItems,
  TProductSkuProps,
  TSkuInfo,
  TProductSkuRecord,
  TProductSkuResponse,
  TProductInfo,
  ResponseData,
  TSamplesCheckResponse,
} from "@/typings";
import Image from "next/image";
import "./index.scss";
import { Spin } from "@arco-design/web-react";
import { Icon } from "@/components/Icons";
import { base64Encode, getUri } from "@/utils/url";
import { usePathname, useRouter } from "next/navigation";
import { useProductSKU, useSamplesCheck } from "@/api/product/use-query";
import { onErrorMessage } from "@/utils/message";
import {
  TrackingBtnId,
  TrackingEventID,
  TrackingEventType,
} from "@/shared/enums/track";
import { useTrackingRecordSave } from "@/api/track/use-query";
import { LoginContext } from "@/app/LoginProvider";
import { RouteStack, SamplesCheckStage } from "@/shared";
import { EventConfig } from "@/hooks/useAddShowcase";
import useRequestSample from "@/hooks/useRequestSample";

interface SkuModalProps {
  skuVisible: boolean;
  samplesLink?: string;
  onClose: () => void;
  onOk: (
    v: boolean,
    sampleCheckData?: ResponseData<TSamplesCheckResponse>
  ) => void;
  productData: TProductInfo;
  eventConfig?: EventConfig;
}

interface TSelectOption {
  productInfo?: TProductSkuRecord;
  selectOptions: TProductSkuPropsItems[];
}

const SkuModal: FC<SkuModalProps> = ({
  skuVisible,
  onClose,
  onOk,
  productData,
  eventConfig,
}) => {
  const router = useRouter();
  const path = usePathname();

  const { mutate: fetchTrackingRecordSave } = useTrackingRecordSave();

  const { mutate: fetchProductSku, isLoading: isSkuLoading } = useProductSKU();

  const { mutate: fetchSamplesCheck, isLoading: isSampleCheckLoading } =
    useSamplesCheck();

  const { handleFreeSample, isAddedShowcase, loading } = useRequestSample({
    productData,
    eventConfig,
  });

  const loginCtx = useContext(LoginContext);

  const [isFirstRender, setIsFirstRender] = useState(true);
  const [productSku, $productSku] = useState<TProductSkuResponse>();
  const [localProductProps, setLocalProductProps] = useState<
    TProductSkuProps[]
  >(productSku?.props || []);

  const [selectProductOption, setSelectOption] = useState<TSelectOption>({
    productInfo: undefined,
    selectOptions: [],
  });

  useEffect(() => {
    if (productData) {
      fetchProductSku(
        { product_id: productData?.product_id || "" },
        {
          onSuccess: (res) => {
            $productSku(res?.data);
            setLocalProductProps(res?.data?.props);
          },
          onError: onErrorMessage,
        }
      );
    }
  }, [fetchProductSku, productData]);

  // 首次进入时，初始化数据
  useEffect(() => {
    if (localProductProps && productSku?.skus && isFirstRender) {
      const productSkuSKeys = Object.keys(productSku?.skus);
      const names =
        productSkuSKeys.find((key) => productSku?.skus[key]?.stock !== 0) || "";
      const productSKUList = names.split("_");
      productSKUList.shift();
      productSKUList.forEach((value) => {
        for (const obj of productSku?.props) {
          const matchedItem = obj.items.find(
            (item) => item.prop_value_id === value
          );
          if (matchedItem) {
            handleOptionChange(obj.prop_id, matchedItem);
          }
        }
      });
      setIsFirstRender(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localProductProps, productSku?.props, isFirstRender, productSku?.skus]);

  // 避免直接修改 selectProductOption，创建新对象
  const handleOptionChange = (
    propId: string,
    selectItem: TProductSkuPropsItems
  ) => {
    if (selectItem.disabled || localProductProps.length === 0 || !productSku)
      return;

    const productSkuSKeys = Object.keys(productSku?.skus);
    if (localProductProps.length === 1) {
      const selectSKUStr = "sku_" + selectItem.prop_value_id;
      const newSelectOption = { ...selectProductOption };
      const newLocalProductProps = localProductProps;
      localProductProps[0]?.items.forEach((item) => {
        if (
          productSkuSKeys.includes("sku_" + item.prop_value_id) &&
          productSku?.skus["sku_" + item.prop_value_id]?.stock !== 0
        ) {
          item.disabled = false;
        } else {
          item.disabled = true;
        }
      });
      for (const key of productSkuSKeys) {
        if (key === selectSKUStr) {
          newLocalProductProps[0]?.items.forEach((item) => {
            if (item.prop_value_id === selectItem.prop_value_id) {
              item.selected = true;
            } else {
              item.selected = false;
            }
          });
          newSelectOption.productInfo = productSku?.skus[selectSKUStr];
          newSelectOption.selectOptions[0] = selectItem;
          break;
        }
      }
      setLocalProductProps(newLocalProductProps);
      setSelectOption(newSelectOption);
      return;
    }

    // 多规格
    setSelectOption((preSelectOption) => {
      let newProductProps = localProductProps;
      const newSelectOption = {
        ...preSelectOption,
        selectOptions: [...preSelectOption.selectOptions],
      };
      newProductProps = localProductProps?.map((propsItem, index) => {
        if (propsItem.prop_id === propId) {
          propsItem.items.forEach((it) => {
            if (it.prop_value_id === selectItem.prop_value_id) {
              it.selected = true;
              newSelectOption.selectOptions[index] = it;
            } else {
              it.selected = false;
            }
          });
        } else {
          propsItem.items.forEach((propsItem) => {
            const index = productSkuSKeys.findIndex(
              (key) =>
                key.includes(propsItem.prop_value_id) &&
                key.includes(selectItem.prop_value_id)
            );
            if (
              index === -1 ||
              (productSkuSKeys[index] &&
                productSku?.skus[productSkuSKeys[index]]?.stock === 0)
            ) {
              propsItem.disabled = true;
            } else {
              propsItem.disabled = false;
            }
          });
        }
        return propsItem;
      });
      const skuKey = productSkuSKeys.filter((key) => {
        return newSelectOption.selectOptions.every((item) => {
          return key.includes(item.prop_value_id);
        });
      });
      setLocalProductProps(newProductProps);
      return {
        productInfo: productSku?.skus[skuKey.toString()],
        selectOptions: [...newSelectOption.selectOptions],
      };
    });
  };

  const selectSkuInfo = useMemo(() => {
    const sku_name: string = selectProductOption.selectOptions.reduce(
      (acc, item) => `${acc}${acc !== "" ? " . " : ""}${item.prop_value}`,
      ""
    );
    const isShowDefaultImg = selectProductOption.selectOptions.some(
      (item) => item.has_img === 1
    );
    const skus: TSkuInfo = {
      sku_name,
      sku_id: selectProductOption?.productInfo?.sku_id ?? "",
      price: selectProductOption?.productInfo?.real_price_value_str ?? "",
      isShowDefaultImg,
    };
    return skus;
  }, [selectProductOption]);

  /** TODO: 发起领样，跳转领样详情页 */
  const requestSample = useCallback(
    (skuInfos: TSkuInfo) => {
      if (skuInfos && productData) {
        const url = `/${RouteStack.Product.product}/${productData?.hash_id}/sample`;
        router.push(
          getUri(url, {
            sku_name: skuInfos.sku_name,
            sku_id: skuInfos.sku_id,
            price: skuInfos.price,
            path: base64Encode(path),
          })
        );
      }
    },
    [productData, router, path]
  );

  /** TODO: 点击 Request Free Sample 埋点 */
  const handleRequestFreeSampleTrack = useCallback(() => {
    if (productData) {
      fetchTrackingRecordSave({
        uid: loginCtx?.userInfo?.uid,
        event_id: TrackingEventID.RequestFreeSample,
        event_type: TrackingEventType.Click,
        product_id: productData?.product_id,
        ...eventConfig,
        btn_id: TrackingBtnId.RequestFreeSample,
      });
    }
  }, [
    productData,
    fetchTrackingRecordSave,
    loginCtx?.userInfo?.uid,
    eventConfig,
  ]);

  /** TODO: 校验平台领样条件 */
  const handleSamplesCheck = useCallback(
    (skuInfos: TSkuInfo) => {
      if (productData) {
        fetchSamplesCheck(
          {
            campaign_id: productData?.campaign_id,
            product_id: productData?.product_id,
            stage: SamplesCheckStage.OnlyCheckRequirements,
          },
          {
            onSuccess: (res) => {
              if (res.code === 0) {
                onOk(false);
                requestSample(skuInfos);
              } else {
                onOk(false, res);
              }
            },
            onError: (error) => {
              onOk(false);
              onErrorMessage(error);
            },
          }
        );
      }
    },
    [fetchSamplesCheck, productData, requestSample, onOk]
  );

  const handleConfirm = useCallback(() => {
    handleRequestFreeSampleTrack();
    handleFreeSample();
  }, [handleRequestFreeSampleTrack, handleFreeSample]);

  useEffect(() => {
    if (isAddedShowcase) {
      handleSamplesCheck(selectSkuInfo);
    }
  }, [isAddedShowcase, selectSkuInfo, handleSamplesCheck]);

  return (
    <Modal
      visible={skuVisible}
      autoFocus={false}
      focusLock={true}
      onCancel={onClose}
      footer={null}
      closeIcon={<Icon.Close width={30} height={30} />}
      className="yf-sku-modal"
    >
      <h2 className="yf-sku-title">SKU</h2>
      <Spin dot loading={isSkuLoading || !localProductProps?.length}>
        <div className="yf-sku-content">
          <div className="flex">
            {!selectSkuInfo.isShowDefaultImg ? (
              <Image
                src={productData?.cover || ""}
                alt={productData.product_name}
                width={71}
                height={71}
                className="rounded-[10px]"
              />
            ) : (
              selectProductOption.selectOptions.map((item) => {
                return (
                  <div className="" key={item.prop_value_id}>
                    <div key={item.prop_value_id}>
                      {item.has_img === 1
                        ? item.image && (
                            <Image
                              src={item.image}
                              alt={item.prop_value}
                              width={71}
                              height={71}
                              className="rounded-[10px]"
                            />
                          )
                        : ""}
                    </div>
                  </div>
                );
              })
            )}
            <div className="project-sku-info">
              <div className="price">
                {selectProductOption?.productInfo?.real_price_value_str}
              </div>
              <div className="name">{selectSkuInfo.sku_name}</div>
            </div>
          </div>
          {localProductProps?.map((propItem) => {
            return (
              <div key={propItem.prop_name}>
                <div className="text-[#1F283C] font-medium text-base mt-[25px] mb-[20px]">
                  {propItem.prop_name}
                </div>
                <div className="flex flex-wrap gap-[20px]">
                  {propItem.items.map((item) => {
                    return (
                      <div
                        key={item.prop_value_id}
                        onClick={() =>
                          handleOptionChange(propItem.prop_id, item)
                        }
                        className={`box cursor-pointer rounded-[10px] ${item.selected ? "border-[1px] border-[#1F283C]" : ""}`}
                      >
                        {item.has_img === 1 ? (
                          item.image && (
                            <Image
                              src={item.image}
                              alt={item.prop_value}
                              width={71}
                              height={71}
                              className={`rounded-[10px] ${item.disabled ? "bg-slate-400 grayscale cursor-not-allowed" : ""}`}
                            />
                          )
                        ) : (
                          <div
                            className={`bg-[#FAFAFA] px-[23px] py-[8px] rounded-2xl ${item.disabled ? "bg-[#FAFAFA] text-[#cad1d9] cursor-not-allowed" : ""}`}
                          >
                            {item.prop_value}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <Button
            loading={loading || isSampleCheckLoading}
            onClick={handleConfirm}
            className="yf-sku-submit-btn cursor-pointer"
          >
            Request Free Sample
          </Button>
        </div>
      </Spin>
    </Modal>
  );
};

export default SkuModal;
